import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { DASH, cardSx } from "./tokens";

type DashCardProps = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  /** Right side of the header (a link or small button). */
  action?: React.ReactNode;
  children?: React.ReactNode;
  /** Pad the body (default true). */
  padded?: boolean;
  sx?: object;
  id?: string;
  testId?: string;
};

/** The one card surface every dashboard panel uses. */
const DashCard: React.FC<DashCardProps> = ({
  title,
  subtitle,
  icon,
  action,
  children,
  padded = true,
  sx,
  id,
  testId,
}) => (
  <Box id={id} sx={{ ...cardSx, overflow: "hidden", ...sx }} data-testid={testId}>
    {(title || action) && (
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ px: { xs: 4, sm: 5 }, pt: { xs: 4, sm: 5 }, pb: children ? 0 : { xs: 4, sm: 5 } }}
      >
        <Stack direction="row" spacing={2.5} alignItems="center" sx={{ minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: "10px",
                bgcolor: DASH.tealTint2,
                color: DASH.teal,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            {title && (
              <Typography component="h3" sx={{ fontWeight: 700, fontSize: 17, color: DASH.teal, lineHeight: 1.3 }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" sx={{ color: DASH.muted, mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
    )}
    {children && <Box sx={padded ? { p: { xs: 4, sm: 5 } } : undefined}>{children}</Box>}
  </Box>
);

export default DashCard;

/** Page-level heading for a tab's middle column. */
export const TabHeading: React.FC<{ title: string; subtitle?: React.ReactNode; action?: React.ReactNode }> = ({
  title,
  subtitle,
  action,
}) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    justifyContent="space-between"
    alignItems={{ xs: "flex-start", sm: "flex-end" }}
    spacing={2}
    sx={{ mb: 5 }}
  >
    <Box>
      <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 22, md: 26 }, color: "#1d2b2b", lineHeight: 1.2 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: DASH.muted, mt: 1, maxWidth: 620 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action}
  </Stack>
);

/** Small dashed empty state used inside cards. */
export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  body?: React.ReactNode;
  action?: React.ReactNode;
  testId?: string;
}> = ({ icon, title, body, action, testId }) => (
  <Stack
    alignItems="center"
    textAlign="center"
    spacing={1.5}
    sx={{ border: `1px dashed ${DASH.border}`, borderRadius: "12px", px: 4, py: 6, bgcolor: "#fcfdfd" }}
    data-testid={testId}
  >
    {icon && <Box sx={{ color: DASH.teal, opacity: 0.8, display: "flex" }}>{icon}</Box>}
    <Typography sx={{ fontWeight: 700, color: "#1d2b2b" }}>{title}</Typography>
    {body && (
      <Typography variant="body2" sx={{ color: DASH.muted, maxWidth: 380 }}>
        {body}
      </Typography>
    )}
    {action}
  </Stack>
);
